import { z } from 'zod';

/**
 * The Tenant Admins module's contracts.
 *
 * These schemas are the source of truth for the shapes this module reads and
 * writes. Types come from `z.infer`, never hand-written, so a schema and its
 * type cannot drift apart.
 *
 * Every limit below is the one the service enforces (`packages/tenancy`), so
 * an over-long name is a sentence beside the input rather than a round trip
 * and a 422. The server still validates — this is a courtesy, never the
 * enforcement.
 */

const NAME_MAX = 200;
const EMAIL_MAX = 254;
const PASSWORD_MIN = 8;
const PASSWORD_MAX = 128;

/** The statuses a tenant administrator may be moved between. */
export const TENANT_ADMIN_STATUS_OPTIONS: readonly {
  readonly value: string;
  readonly label: string;
}[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'SUSPENDED', label: 'Suspended' },
] as const;

const statusValues = TENANT_ADMIN_STATUS_OPTIONS.map((option) => option.value) as [
  string,
  ...string[],
];

/**
 * One row in the tenant administrators list.
 *
 * **Only `id` is guaranteed.** The list sends `?fields=` derived from the
 * columns the reader has chosen and the service honours it strictly, so a
 * field behind a hidden column is genuinely absent from the response.
 * Declaring any of the others required would make the whole list fail to parse
 * the moment someone hides a column.
 */
export const tenantAdminRow = z.object({
  id: z.string(),
  tenantId: z.string(),
  email: z.string().optional(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  status: z.string().optional(),
  createdAt: z.string().optional(),
});

export type TenantAdminRow = z.infer<typeof tenantAdminRow>;

/**
 * The full tenant administrator, as the detail screen and the edit form read it.
 *
 * Required here rather than optional, unlike the row above: the detail
 * endpoint is called without `?fields=` and therefore always projects
 * everything.
 */
export const tenantAdminDetail = z.object({
  id: z.string(),
  tenantId: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string(),
  status: z.string(),
  createdAt: z.string(),
});

export type TenantAdminDetail = z.infer<typeof tenantAdminDetail>;

/** The shape returned by a successful create. */
export const createdTenantAdmin = z.object({
  id: z.string(),
  tenantId: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  status: z.string(),
  setupToken: z.string(),
});

export type CreatedTenantAdmin = z.infer<typeof createdTenantAdmin>;

/**
 * What the create and edit forms collect.
 *
 * One schema for both, because they are the same fields under the same rules;
 * what differs is which of them the service will accept, and that is the
 * payload builder's job rather than a second schema to keep in step. Every
 * value is a string: a controlled input cannot hold `null`.
 */
export const tenantAdminFormInput = z.object({
  tenantId: z.uuid(),
  email: z.email('Enter a valid email address').max(EMAIL_MAX),
  password: z.string().min(PASSWORD_MIN).max(PASSWORD_MAX).optional(),
  firstName: z
    .string()
    .trim()
    .min(1, 'First name is required')
    .max(NAME_MAX, `Keep the first name under ${String(NAME_MAX)} characters`),
  lastName: z
    .string()
    .trim()
    .max(NAME_MAX, `Keep the last name under ${String(NAME_MAX)} characters`),
  status: z.enum(statusValues),
});

export type TenantAdminFormInput = z.infer<typeof tenantAdminFormInput>;
